import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { AuthState, AuthContextValue, LoginCredentials, User } from '../types/auth.types';
import { authService } from '../services/authService';

// Initial state
const initialState: AuthState = {
  user: null,
  accessToken: null,
  refreshToken: null,
  isAuthenticated: false,
  loading: true,
  error: null,
};

// Action types
type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: { user: User; accessToken: string; refreshToken: string } }
  | { type: 'AUTH_FAILURE'; payload: string }
  | { type: 'AUTH_LOGOUT' }
  | { type: 'AUTH_UPDATE_USER'; payload: Partial<User> }
  | { type: 'AUTH_CLEAR_ERROR' }
  | { type: 'AUTH_SET_LOADING'; payload: boolean };

// Reducer
function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        loading: true,
        error: null,
      };

    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        accessToken: action.payload.accessToken,
        refreshToken: action.payload.refreshToken,
        isAuthenticated: true,
        loading: false,
        error: null,
      };

    case 'AUTH_FAILURE':
      return {
        ...state,
        user: null,
        accessToken: null,
        refreshToken: null,
        isAuthenticated: false,
        loading: false,
        error: action.payload,
      };

    case 'AUTH_LOGOUT':
      return {
        ...state,
        user: null,
        accessToken: null,
        refreshToken: null,
        isAuthenticated: false,
        loading: false,
        error: null,
      };

    case 'AUTH_UPDATE_USER':
      return {
        ...state,
        user: state.user ? { ...state.user, ...action.payload } : null,
      };

    case 'AUTH_CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };

    case 'AUTH_SET_LOADING':
      return {
        ...state,
        loading: action.payload,
      };

    default:
      return state;
  }
}

// Create context
const AuthContext = createContext<AuthContextValue | null>(null);

// Provider component
export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Initialize authentication state on app load
  useEffect(() => {
    const initializeAuth = async () => {
      console.log('🔐 AuthContext: Initializing authentication state');
      
      try {
        dispatch({ type: 'AUTH_SET_LOADING', payload: true });

        // Check if user is already authenticated
        const accessToken = authService.getStoredAccessToken();
        const refreshToken = authService.getStoredRefreshToken();
        const user = authService.getStoredUser();

        if (accessToken && refreshToken && user) {
          console.log('🔐 AuthContext: Found stored authentication data');
          
          // Validate the stored tokens by getting current user
          try {
            const currentUser = await authService.getCurrentUser();
            dispatch({
              type: 'AUTH_SUCCESS',
              payload: {
                user: currentUser,
                accessToken,
                refreshToken,
              },
            });
            console.log('✅ AuthContext: Authentication restored successfully');
          } catch (error) {
            console.log('⚠️ AuthContext: Stored tokens invalid, clearing auth data');
            await authService.logout();
            dispatch({ type: 'AUTH_LOGOUT' });
          }
        } else {
          console.log('🔐 AuthContext: No stored authentication data found');
          dispatch({ type: 'AUTH_LOGOUT' });
        }
      } catch (error) {
        console.error('❌ AuthContext: Failed to initialize auth:', error);
        dispatch({ type: 'AUTH_LOGOUT' });
      } finally {
        dispatch({ type: 'AUTH_SET_LOADING', payload: false });
      }
    };

    initializeAuth();
  }, []);

  // Login function
  const login = React.useCallback(async (credentials: LoginCredentials): Promise<void> => {
    try {
      console.log('🔐 AuthContext: Starting login process');
      dispatch({ type: 'AUTH_START' });

      const response = await authService.login(credentials);
      
      dispatch({
        type: 'AUTH_SUCCESS',
        payload: {
          user: response.user,
          accessToken: response.accessToken,
          refreshToken: response.refreshToken,
        },
      });

      console.log('✅ AuthContext: Login successful');
    } catch (error) {
      console.error('❌ AuthContext: Login failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      dispatch({ type: 'AUTH_FAILURE', payload: errorMessage });
      throw error;
    }
  }, []);

  // Logout function
  const logout = React.useCallback(async (): Promise<void> => {
    try {
      console.log('🔓 AuthContext: Starting logout process');
      dispatch({ type: 'AUTH_START' });

      await authService.logout();
      dispatch({ type: 'AUTH_LOGOUT' });

      console.log('✅ AuthContext: Logout successful');
    } catch (error) {
      console.error('❌ AuthContext: Logout error:', error);
      // Even if logout fails on the server, clear local state
      dispatch({ type: 'AUTH_LOGOUT' });
    }
  }, []);

  // Refresh authentication
  const refreshAuth = React.useCallback(async (): Promise<void> => {
    try {
      console.log('🔄 AuthContext: Refreshing authentication');

      const response = await authService.refreshToken();
      
      // Update only the access token, keep existing user and refresh token
      if (state.user && state.refreshToken) {
        dispatch({
          type: 'AUTH_SUCCESS',
          payload: {
            user: state.user,
            accessToken: response.accessToken,
            refreshToken: state.refreshToken,
          },
        });
        console.log('✅ AuthContext: Authentication refreshed');
      } else {
        throw new Error('No user or refresh token available');
      }
    } catch (error) {
      console.error('❌ AuthContext: Refresh auth failed:', error);
      dispatch({ type: 'AUTH_LOGOUT' });
      throw error;
    }
  }, [state.user, state.refreshToken]);

  // Update user profile
  const updateUser = React.useCallback(async (updates: Partial<User>): Promise<void> => {
    try {
      console.log('👤 AuthContext: Updating user profile');

      const updatedUser = await authService.updateUser(updates);
      dispatch({ type: 'AUTH_UPDATE_USER', payload: updatedUser });

      console.log('✅ AuthContext: User profile updated');
    } catch (error) {
      console.error('❌ AuthContext: Update user failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Update failed';
      dispatch({ type: 'AUTH_FAILURE', payload: errorMessage });
      throw error;
    }
  }, []);

  // Clear error
  const clearError = React.useCallback((): void => {
    dispatch({ type: 'AUTH_CLEAR_ERROR' });
  }, []);

  // Context value
  const contextValue: AuthContextValue = React.useMemo(() => ({
    ...state,
    login,
    logout,
    refreshAuth,
    updateUser,
    clearError,
  }), [state, login, logout, refreshAuth, updateUser, clearError]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

// Hook to use auth context
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
}

// Higher-order component for protected routes
export function withAuth<P extends object>(
  Component: React.ComponentType<P>
): React.ComponentType<P> {
  return function AuthenticatedComponent(props: P) {
    const { isAuthenticated, loading } = useAuth();
    
    if (loading) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading...</p>
          </div>
        </div>
      );
    }
    
    if (!isAuthenticated) {
      // Redirect to login page
      window.location.href = '/login';
      return null;
    }
    
    return <Component {...props} />;
  };
}

// Permission checking hooks
export function usePermissions() {
  const { user } = useAuth();

  const hasRole = (role: string): boolean => {
    return user?.role === role;
  };

  const hasAnyRole = (roles: string[]): boolean => {
    return user ? roles.includes(user.role) : false;
  };

  const canAccess = (resource: string): boolean => {
    if (!user) return false;

    // Role-based access control
    const rolePermissions: Record<string, string[]> = {
      admin: ['*'], // All resources
      manager: ['dashboard', 'bookings', 'assignments', 'fleet', 'finance', 'config'],
      dispatcher: ['dashboard', 'bookings', 'assignments', 'fleet'],
      finance: ['dashboard', 'bookings', 'finance'],
      viewer: ['dashboard'], // Read-only
    };

    const userPermissions = rolePermissions[user.role] || [];
    return userPermissions.includes('*') || userPermissions.includes(resource);
  };

  const isAdmin = (): boolean => hasRole('admin');
  const isManager = (): boolean => hasRole('manager');
  const isDispatcher = (): boolean => hasRole('dispatcher');
  const isFinance = (): boolean => hasRole('finance');
  const isViewer = (): boolean => hasRole('viewer');

  return {
    hasRole,
    hasAnyRole,
    canAccess,
    isAdmin,
    isManager,
    isDispatcher,
    isFinance,
    isViewer,
    role: user?.role,
  };
}

// Session management hooks
export function useSession() {
  const { user, accessToken, refreshToken, isAuthenticated } = useAuth();

  const getSessionInfo = () => ({
    userId: user?.id,
    email: user?.email,
    role: user?.role,
    isAuthenticated,
    hasAccessToken: !!accessToken,
    hasRefreshToken: !!refreshToken,
    lastLoginAt: user?.lastLoginAt,
  });

  const isSessionValid = (): boolean => {
    return isAuthenticated && !!accessToken && !!user;
  };

  const getSessionDuration = (): number | null => {
    if (!user?.lastLoginAt) return null;
    
    const loginTime = new Date(user.lastLoginAt).getTime();
    const now = Date.now();
    return Math.floor((now - loginTime) / 1000); // Duration in seconds
  };

  return {
    getSessionInfo,
    isSessionValid,
    getSessionDuration,
    sessionData: getSessionInfo(),
  };
}

export default AuthContext;